// user-request-form.js
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { getUserEmail } from "../../services/apiClient";
import UserService from "../../services/userService";
import BusinessUnitService from "../../services/businessUnitService";
import ServiceTypeService from "../../services/serviceTypeService";
import RequestService from "../../services/requestService";
import ProviderService from "../../services/providerservice";
import LocationService from "../../services/locationService";
import MessengerService from "../../services/messengerService";
import { showLoading, hideLoading, showSuccess, showError } from "../../components/utils/swal-helpers";
import { modelDataRequest, isDocumentDelivery, buildValidationSchema, buildDestinationValidationSchema } from "./constants";
import { DecodeName } from "../../components/utils/format-value";
import {
  DAY_CODE,
  extractTimeHHMM,
  isOtherPickupDetail,
  isOtherBuildingDetail,
  isOtherProviderBranch,
  buildReplacementsString,
} from "./user-request-form.utils";

const buildRecurringPayload = (config) => {
  const frequencyTypeMap = { week: 2, month: 3 };
  const frequency_type = frequencyTypeMap[config.freq_unit] || 0;

  let recurrence_type = 0;
  if (config.freq_unit === "week")             recurrence_type = 20;
  if (config.monthly_type === "day_of_month")  recurrence_type = 31;
  if (config.monthly_type === "week_of_month") recurrence_type = 32;
  if (config.monthly_type === "last_week")     recurrence_type = 32;

  const countWeekdayOccurrencesInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weekday = date.getDay();
    let count = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      if (new Date(year, month, day).getDay() === weekday) count++;
    }
    return count;
  };

  const recurrence_detail = config.freq_unit === "week"
    ? config.repeat_days?.map((i) => DAY_CODE[i]).join(", ") || ""
    : (() => {
        const d = new Date(config.start_date);
        const dayOfMonth = d.getDate();
        const dayCode = DAY_CODE[d.getDay() === 0 ? 6 : d.getDay() - 1];
        if (config.monthly_type === "day_of_month") return String(dayOfMonth);
        if (config.monthly_type === "week_of_month") {
          const weekNum = Math.ceil(dayOfMonth / 7);
          return `${dayCode}:${weekNum}`;
        }
        if (config.monthly_type === "last_week") {
          const totalOccurrences = countWeekdayOccurrencesInMonth(d);
          return `${dayCode}:${totalOccurrences}`;
        }
        return "";
      })();

  return {
    start_date: config.start_date,
    end_date: config.end_date,
    every: Number(config.freq_num) || 1,
    frequency_type,
    recurrence_type,
    recurrence_detail,
    replacements_date: buildReplacementsString(config.replacements),
    status: 1,
    create_by: getUserEmail(),
  };
};

const fetchMessengerById = async (id_messenger) => {
  if (!id_messenger) return null;
  try {
      const res = await MessengerService.SearchMessenger({ status: 1 });
      const all = res.data.result || [];
      return all.find(m => m.id === id_messenger) ?? null;
  } catch (e) {
      console.error("fetchMessengerById error:", e);
      return null;
  }
};

export const useUserRequestForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const decodedId = id ? atob(id).split("::")[0] : "0";
  const isViewMode = decodedId !== "0";

  const [dataDetail, setDataDetail]   = useState({ ...modelDataRequest });
  const [dataUser,   setDataUser]     = useState(null);
  const [formIsDisabled, setFormIsDisabled] = useState(false);

  const [showRecurring, setShowRecurring] = useState(false);
  const [recurringConfig, setRecurringConfig] = useState(null);
  const [showRecurringPreview,  setShowRecurringPreview]  = useState(false);
  const [recurringPreviewDates, setRecurringPreviewDates] = useState([]);
  const [createdRefNo, setCreatedRefNo] = useState(null);
  const [refNo, setRefNo] = useState("");
  const [isCloneData, setIsCloneData] = useState(false);
  const [cloneRefNo, setCloneRefNo] = useState("");

  const [pickupLocations,  setPickupLocations]  = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [serviceTypes,     setServiceTypes]     = useState([]);

  // ── Banking สำหรับ main form ──────────────────────────────────────────────
  const [providers,        setProviders]        = useState([]);
  const [providerBranches, setProviderBranches] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingBranches,  setLoadingBranches]  = useState(false);

  // ── Banking สำหรับ extra destinations (แยกต่างหาก) ───────────────────────
  const [destProviders,        setDestProviders]        = useState([]);
  const [destProviderBranches, setDestProviderBranches] = useState([]);
  const [loadingDestProviders, setLoadingDestProviders] = useState(false);
  const [loadingDestBranches,  setLoadingDestBranches]  = useState(false);

  const [subServiceTypes,        setSubServiceTypes]        = useState([]);
  const [loadingSubServiceTypes, setLoadingSubServiceTypes] = useState(false);

  const [buildings,        setBuildings]        = useState([]);
  const [provinces,        setProvinces]        = useState([]);
  const [districts,        setDistricts]        = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  const [destDistricts,        setDestDistricts]        = useState({});
  const [loadingDestDistricts, setLoadingDestDistricts] = useState({});

  // ── Transaction (service_type = 3) ───────────────────────────────────────
  const [transactionProviders,        setTransactionProviders]        = useState([]);
  const [loadingTransactionProviders, setLoadingTransactionProviders] = useState(false);
  const [transactionBranches,         setTransactionBranches]         = useState([]);
  const [loadingTransactionBranches,  setLoadingTransactionBranches]  = useState(false);

  const [requestDetails, setRequestDetails] = useState([]);

  const MAX_EXTRA_DESTINATIONS = 2;

  const emptyDestination = () => ({
    _id: Date.now() + Math.random(),
    seq: null,
    messenger: null,
    service_date:        "",
    pickup_location:     "",
    other_pickup_name:   "",
    service_type:        "",
    delivery_type:       null,
    document_secret:     null,
    delivery_section:    "",
    is_complete_today:   null,
    time_complete_today: "",
    receiver_name:        "",
    receiver_tel:         "",
    receiver_company:     "",
    receiver_department:  "",
    receiver_building:    "",
    receiver_floor:       "",
    receiver_province:    "",
    receiver_district:    "",
    receiver_map:         "",
    id_provider:          "",
    id_provider_branch:   "",
    provider_branch_name: "",
    id_sub_service_type3: "",
    note:                 "",
    other_branch_name:    "",
    other_building_name:  "",
  });

  const [destinations, setDestinations] = useState([]);

  const applyCloneDestinations = async (source) => {
    const extraDetails = source.request_details?.slice(1, 1 + MAX_EXTRA_DESTINATIONS) ?? [];
    if (extraDetails.length === 0) {
      setDestinations([]);
      return;
    }

    const extraDests = await Promise.all(
      extraDetails.map(async (d) => {
        const destId = Date.now() + Math.random();
        if (d.destination_id_province) fetchDestDistricts(destId, d.destination_id_province);
        if (d.id_service_type === 2) {
          fetchDestProviders();
          if (d.destination_id_provider) fetchDestProviderBranches(d.destination_id_provider);
        }
        if (d.id_service_type === 3) {
          fetchSubServiceTypes();
          fetchTransactionProviders(d.id_sub_service_type);
          if (d.destination_id_provider) fetchTransactionBranches(d.destination_id_provider);
        }

        const isDestOtherPickup = isOtherPickupDetail(d);
        const isDestOtherBuilding = isOtherBuildingDetail(d);
        const isDestOtherBranch = isOtherProviderBranch(d.id_service_type, d.destination_id_provider_branch);

        return {
          ...emptyDestination(),
          _id:                  destId,
          seq:                  null,
          pickup_location:      isDestOtherPickup ? "other" : (source.sender_id_bu ?? ""),
          other_pickup_name:    isDestOtherPickup ? (d.origin_building_name ?? "") : "",
          service_type:         d.id_service_type ?? "",
          delivery_type:        d.id_sub_service_type > 0 ? d.id_sub_service_type : null,
          document_secret:      source.document_type ?? null,
          delivery_section:     source.delivery_section ?? "",
          is_complete_today:    d.complete_time_type != null ? Number(d.complete_time_type) : null,
          time_complete_today:  extractTimeHHMM(d.complete_time_specific),
          receiver_name:        d.destination_contact_name ?? "",
          receiver_tel:         d.destination_phone_number ?? "",
          receiver_company:     d.destination_company ?? "",
          receiver_department:  d.destination_department ?? "",
          receiver_floor:       d.destination_floor ?? "",
          receiver_building:    isDestOtherBuilding ? "other" : String(d.destination_id_building || ""),
          other_building_name:  isDestOtherBuilding ? (d.destination_name ?? "") : "",
          receiver_province:    String(d.destination_id_province || ""),
          receiver_district:    String(d.destination_id_district || ""),
          receiver_map:         d.destination_map ?? "",
          note:                 d.note ?? "",
          id_provider:          String(d.destination_id_provider || ""),
          id_provider_branch:   isDestOtherBranch ? "other" : String(d.destination_id_provider_branch ?? ""),
          other_branch_name:    isDestOtherBranch ? (d.destination_name || d.destination_contact_name || "") : "",
          id_sub_service_type3: d.id_sub_service_type > 0 ? String(d.id_sub_service_type) : "",
          messenger:            null,
        };
      })
    );

    setDestinations(extraDests);
  };

  const addDestination = () => {
    if (destinations.length >= MAX_EXTRA_DESTINATIONS) return;
    setDestinations((prev) => [...prev, emptyDestination()]);
  };
  const removeDestination = (_id) =>
    setDestinations((prev) => prev.filter((d) => d._id !== _id));
  const updateDestination = (_id, field, value) =>
    setDestinations((prev) =>
      prev.map((d) => (d._id === _id ? { ...d, [field]: value } : d))
    );

  // ── Validation สำหรับ destinations (จุดหมายที่ 2, 3) ──────────────────────
  const [destinationErrors, setDestinationErrors] = useState({});

  const validateDestinations = () => {
    const schema = buildDestinationValidationSchema();
    const errorsMap = {};
    let isValid = true;

    destinations.forEach((dest) => {
      try {
        schema.validateSync(
          { ...dest, service_date: validation.values.service_date },
          { abortEarly: false }
        );
      } catch (err) {
        isValid = false;
        const fieldErrors = {};
        (err.inner || []).forEach((e) => {
          if (e.path && !fieldErrors[e.path]) fieldErrors[e.path] = e.message;
        });
        errorsMap[dest._id] = fieldErrors;
      }
    });

    setDestinationErrors(errorsMap);
    return isValid;
  };

  const showDestErr = (destId, field) => {
    const msg = destinationErrors[destId]?.[field];
    return msg
      ? <div className="invalid-feedback" style={{ display: "block" }}>{msg}</div>
      : null;
  };

  const setDestinationErrorsField = (destId, field, message) => {
    setDestinationErrors((prev) => {
      const current = { ...(prev[destId] || {}) };
      if (message) {
        current[field] = message;
      } else {
        delete current[field];
      }
      return { ...prev, [destId]: current };
    });
  };

  const fetchUser = async () => {
    try {
      const email = getUserEmail();
      const res   = await UserService.SearchUser({ email });
      setDataUser(res.data.result[0]);
    } catch (err) {
      console.error("fetchUser error:", err.response?.data);
    }
  };

  const fetchPickupLocations = async () => {
    setLoadingLocations(true);
    try {
      const res = await BusinessUnitService.SearchBusinessUnit({ status: 1 });
      setPickupLocations(res.data.result || []);
    } catch (err) {
      console.error("fetchPickupLocations error:", err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const res = await ServiceTypeService.SearchServiceType({ status: 1 });
      setServiceTypes(res.data.result || []);
    } catch (err) {
      console.error("fetchServiceTypes error:", err);
    }
  };

  // ── main form providers ───────────────────────────────────────────────────
  const fetchProviders = async () => {
    setLoadingProviders(true);
    try {
      const res = await ProviderService.SearchProvider({ id_service_type: 2, status: 1 });
      setProviders(res.data.result || []);
    } catch (err) {
      console.error("fetchProviders error:", err);
    } finally {
      setLoadingProviders(false);
    }
  };

  const fetchProviderBranches = async (idProvider) => {
    setLoadingBranches(true);
    try {
      const res = await ProviderService.SearchProviderBranch({
        id_provider:     idProvider,
        id_service_type: 2,
        status:          1,
      });
      setProviderBranches(res.data.result || []);
    } catch (err) {
      console.error("fetchProviderBranches error:", err);
    } finally {
      setLoadingBranches(false);
    }
  };

  // ── extra destinations providers (แยกต่างหาก) ────────────────────────────
  const fetchDestProviders = async () => {
    setLoadingDestProviders(true);
    try {
      const res = await ProviderService.SearchProvider({ id_service_type: 2, status: 1 });
      setDestProviders(res.data.result || []);
    } catch (err) {
      console.error("fetchDestProviders error:", err);
    } finally {
      setLoadingDestProviders(false);
    }
  };

  const fetchDestProviderBranches = async (idProvider) => {
    setLoadingDestBranches(true);
    try {
      const res = await ProviderService.SearchProviderBranch({
        id_provider:     idProvider,
        id_service_type: 2,
        status:          1,
      });
      setDestProviderBranches(res.data.result || []);
    } catch (err) {
      console.error("fetchDestProviderBranches error:", err);
    } finally {
      setLoadingDestBranches(false);
    }
  };

  const fetchSubServiceTypes = async () => {
    setLoadingSubServiceTypes(true);
    try {
      const res = await ServiceTypeService.SearchSubServiceType({ id_service_type: 3, status: 1 });
      setSubServiceTypes(res.data.result || []);
    } catch (err) {
      console.error("fetchSubServiceTypes error:", err);
    } finally {
      setLoadingSubServiceTypes(false);
    }
  };

  const fetchBuildings = async () => {
    try {
      const res = await LocationService.SearchBuilding({ status: 1 });
      setBuildings(res.data.result || []);
    } catch (err) {
      console.error("fetchBuildings error:", err);
    }
  };

  const fetchProvinces = async () => {
    try {
      const res = await LocationService.SearchProvince({ status: 1 });
      setProvinces(res.data.result || []);
    } catch (err) {
      console.error("fetchProvinces error:", err);
    }
  };

  const fetchDistricts = async (idProvince) => {
    setLoadingDistricts(true);
    try {
      const res = await LocationService.SearchDistrict({
        ...(idProvince ? { id_province: idProvince } : {}),
        status: 1,
      });
      setDistricts(res.data.result || []);
    } catch (err) {
      console.error("fetchDistricts error:", err);
    } finally {
      setLoadingDistricts(false);
    }
  };

  const fetchDestDistricts = async (destId, idProvince) => {
    setLoadingDestDistricts((prev) => ({ ...prev, [destId]: true }));
    try {
      const res = await LocationService.SearchDistrict({
        ...(idProvince ? { id_province: idProvince } : {}),
        status: 1,
      });
      setDestDistricts((prev) => ({ ...prev, [destId]: res.data.result || [] }));
    } catch (err) {
      console.error("fetchDestDistricts error:", err);
    } finally {
      setLoadingDestDistricts((prev) => ({ ...prev, [destId]: false }));
    }
  };

  const transactionProvidersRequestRef = useRef(0);

  const fetchTransactionProviders = async (idSubServiceType) => {
    const requestId = ++transactionProvidersRequestRef.current;
    setLoadingTransactionProviders(true);
    setTransactionBranches([]);
    try {
      const res = await ProviderService.SearchProvider({
        id_service_type:     3,
        id_sub_service_type: idSubServiceType,
        status:              1,
      });
      if (requestId !== transactionProvidersRequestRef.current) return;
      setTransactionProviders(res.data.result || []);
    } catch (err) {
      console.error("fetchTransactionProviders error:", err);
    } finally {
      if (requestId === transactionProvidersRequestRef.current) {
        setLoadingTransactionProviders(false);
      }
    }
  };

  const fetchTransactionBranches = async (idProvider) => {
    setLoadingTransactionBranches(true);
    setTransactionBranches([]);
    try {
      const res = await ProviderService.SearchProviderBranch({
        id_provider:     idProvider,
        id_service_type: 3,
        status:          1,
      });
      setTransactionBranches(res.data.result || []);
    } catch (err) {
      console.error("fetchTransactionBranches error:", err);
    } finally {
      setLoadingTransactionBranches(false);
    }
  };

  const loadRequestById = async (requestId) => {
    try {
      setRefNo("");
      setDataDetail({ ...modelDataRequest });

      const res = await RequestService.SearchRequest({ id: parseInt(requestId) });
      const results = res.data.result;
      if (!results?.length) return;

      const source = results.find(r => r.id === parseInt(requestId));
      if (!source) return;

      const MOCK_RETURN = false;
      if (MOCK_RETURN) {
        source.id_workflow_status = 5;
        source.admin_remark = "กรุณาระบุสาขาให้ถูกต้อง และตรวจสอบสถานการณ์วิกฤติ — สาขา 'อื่นๆ' ไม่สามารถใช้ได้สำหรับธุรกรรมประเภทนี้";
      }

      setRefNo(source.ref_no || "");

      // ── load recurring config ──
      if (source.request_recurring) {
          const r = source.request_recurring;
          const freqUnitMap = { 2: "week", 3: "month" };
          const dayCodeToIndex = { MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6 };

          setRecurringConfig({
              start_date:   r.start_date?.slice(0, 10) ?? null,
              end_date:     r.end_date?.slice(0, 10) ?? null,
              freq_num:     r.every ?? 1,
              freq_unit:    freqUnitMap[r.frequency_type] ?? "week",
              repeat_days:  r.recurrence_detail
                  ? r.recurrence_detail.split(", ").map(c => dayCodeToIndex[c]).filter(v => v !== undefined)
                  : [],
              monthly_type: r.recurrence_type === 31 ? "day_of_month"
                          : r.recurrence_type === 32 ? "week_of_month"
                          : "",
              days: [],
          });
      }

      setRequestDetails(source.request_details || []);

      const detail = source.request_details?.[0] ?? {};

      if (detail.destination_id_province) fetchDistricts(detail.destination_id_province);
      if (detail.id_service_type === 2) {
        fetchProviders();
        if (detail.destination_id_provider) {
          fetchProviderBranches(detail.destination_id_provider);
        }
      }
      if (detail.id_service_type === 3) {
        fetchSubServiceTypes();
        fetchTransactionProviders(detail.id_sub_service_type);
        if (detail.destination_id_provider) {
          fetchTransactionBranches(detail.destination_id_provider);
        }
      }

      const mainMessenger = source.messenger ?? await fetchMessengerById(detail.id_messenger);

      const isOtherPickup = isOtherPickupDetail(detail);

      const isOtherBuilding = isOtherBuildingDetail(detail);

      const isOtherBranch = isOtherProviderBranch(detail.id_service_type, detail.destination_id_provider_branch);

      setDataDetail({
        id:                   source.id,
        seq:                  detail.seq ?? null,
        service_date:         source.service_date?.slice(0, 10) ?? "",
        pickup_location:      isOtherPickup ? "other" : (source.sender_id_bu ?? ""),
        other_pickup_name:    isOtherPickup ? (detail.origin_building_name ?? "") : "",
        document_type:        source.document_type ?? "",
        delivery_section:     source.delivery_section ?? "",
        is_complete_today:    detail.complete_time_type != null ? Number(detail.complete_time_type) : null,
        time_complete_today:  extractTimeHHMM(detail.complete_time_specific),
        document_secret:      source.document_type ?? null,
        service_type:         detail.id_service_type ?? "",
        delivery_type:        detail.id_sub_service_type > 0 ? detail.id_sub_service_type : null,
        receiver_name:        detail.destination_contact_name ?? "",
        receiver_tel:         detail.destination_phone_number ?? "",
        receiver_company:     detail.destination_company ?? "",
        receiver_department:  detail.destination_department ?? "",
        receiver_floor:       detail.destination_floor ?? "",
        receiver_building:    isOtherBuilding ? "other" : String(detail.destination_id_building || ""),
        other_building_name:  isOtherBuilding ? (detail.destination_name ?? "") : "",
        receiver_province:    String(detail.destination_id_province || ""),
        receiver_district:    String(detail.destination_id_district || ""),
        receiver_map:         detail.destination_map ?? "",
        note:                 detail.note ?? "",
        id_provider:          String(detail.destination_id_provider || ""),
        id_provider_branch:   isOtherBranch ? "other" : String(detail.destination_id_provider_branch ?? ""),
        other_branch_name:    isOtherBranch ? (detail.destination_name || detail.destination_contact_name || "") : "",
        id_sub_service_type3: detail.id_sub_service_type > 0 ? String(detail.id_sub_service_type) : "",
        messenger:            mainMessenger,
        id_workflow_status:   source.id_workflow_status ?? 0,
        admin_remark: source.admin_remark ?? "",
      });

      const extraDetails = source.request_details?.slice(1) ?? [];
      if (extraDetails.length > 0) {
        const extraDestsWithMessenger = await Promise.all(
          extraDetails.map(async (d) => {
            const destId = Date.now() + Math.random();
            if (d.destination_id_province) fetchDestDistricts(destId, d.destination_id_province);
            if (d.id_service_type === 2) {
              fetchDestProviders();
              if (d.destination_id_provider) {
                fetchDestProviderBranches(d.destination_id_provider);
              }
            }
            if (d.id_service_type === 3) {
              fetchSubServiceTypes();
              fetchTransactionProviders(d.id_sub_service_type);
              if (d.destination_id_provider) fetchTransactionBranches(d.destination_id_provider);
            }

            const destMessenger = await fetchMessengerById(d.id_messenger);

            const isDestOtherPickup = isOtherPickupDetail(d);
            const isDestOtherBuilding = isOtherBuildingDetail(d);
            const isDestOtherBranch = isOtherProviderBranch(d.id_service_type, d.destination_id_provider_branch);

            return {
              ...emptyDestination(),
              _id:                  destId,
              seq:                  d.seq ?? null,
              service_date:         source.service_date?.slice(0, 10) ?? "",
              pickup_location:      isDestOtherPickup ? "other" : (source.sender_id_bu ?? ""),
              other_pickup_name:    isDestOtherPickup ? (d.origin_building_name ?? "") : "",
              service_type:         d.id_service_type ?? "",
              delivery_type:        d.id_sub_service_type > 0 ? d.id_sub_service_type : null,
              document_secret:      source.document_type ?? null,
              delivery_section:     source.delivery_section ?? "",
              is_complete_today:    d.complete_time_type != null ? Number(d.complete_time_type) : null,
              time_complete_today:  extractTimeHHMM(d.complete_time_specific),
              receiver_name:        d.destination_contact_name ?? "",
              receiver_tel:         d.destination_phone_number ?? "",
              receiver_company:     d.destination_company ?? "",
              receiver_department:  d.destination_department ?? "",
              receiver_floor:       d.destination_floor ?? "",
              receiver_building:    isDestOtherBuilding ? "other" : String(d.destination_id_building || ""),
              other_building_name:  isDestOtherBuilding ? (d.destination_name ?? "") : "",
              receiver_province:    String(d.destination_id_province || ""),
              receiver_district:    String(d.destination_id_district || ""),
              receiver_map:         d.destination_map ?? "",
              note:                 d.note ?? "",
              id_provider:          String(d.destination_id_provider || ""),
              id_provider_branch:   isDestOtherBranch ? "other" : String(d.destination_id_provider_branch ?? ""),
              other_branch_name:    isDestOtherBranch ? (d.destination_name || d.destination_contact_name || "") : "",
              id_sub_service_type3: d.id_sub_service_type > 0 ? String(d.id_sub_service_type) : "",
              messenger:            destMessenger,
            };
          })
        );

        setDestinations(extraDestsWithMessenger);
      }

      const isAdminActioned = source.id_workflow_status > 5;
      setFormIsDisabled(isAdminActioned);
    } catch (err) {
      console.error("loadRequestById error:", err);
    }
  };

  useEffect(() => {
    fetchUser();
    fetchPickupLocations();
    fetchServiceTypes();
    fetchBuildings();
    fetchProvinces();
    fetchDistricts();

    if (isViewMode) {
      loadRequestById(decodedId);
    }
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id:                   dataDetail.id               || 0,
      seq:                  dataDetail.seq              || null,
      service_date:         dataDetail.service_date     || "",
      pickup_location:      dataDetail.pickup_location  || "",
      other_pickup_name:    dataDetail.other_pickup_name    || "",
      service_type:         dataDetail.service_type     || "",
      delivery_type:        dataDetail.delivery_type    || null,
      document_secret:      dataDetail.document_secret  || null,
      delivery_section:     dataDetail.delivery_section || "",
      other_branch_name:    dataDetail.other_branch_name    || "",
      other_building_name:  dataDetail.other_building_name  || "",
      receiver_name:        dataDetail.receiver_name        || "",
      receiver_tel:         dataDetail.receiver_tel         || "",
      receiver_company:     dataDetail.receiver_company     || "",
      receiver_department:  dataDetail.receiver_department  || "",
      receiver_building:    dataDetail.receiver_building    || "",
      receiver_floor:       dataDetail.receiver_floor       || "",
      receiver_province:    dataDetail.receiver_province    || "",
      receiver_district:    dataDetail.receiver_district    || "",
      receiver_map:         dataDetail.receiver_map         || "",
      is_complete_today:    dataDetail.is_complete_today    || null,
      time_complete_today:  dataDetail.time_complete_today  || "",
      note:                 dataDetail.note                 || "",
      id_provider:          dataDetail.id_provider          || "",
      id_provider_branch:   dataDetail.id_provider_branch   || "",
      id_sub_service_type3: dataDetail.id_sub_service_type3 || "",
      messenger:            dataDetail.messenger            || null,
      provider_branch_name: dataDetail.provider_branch_name || "",
      id_workflow_status:   dataDetail.id_workflow_status   || 0,
      admin_remark:         dataDetail.admin_remark         || "",
    },
    validationSchema: buildValidationSchema(),

    onSubmit: async (values) => {
      // ── validate destinations (จุดหมายที่ 2, 3) ก่อน build payload ──
      if (!validateDestinations()) return;

      const payload = buildMainPayload();

      try {
        showLoading();
        const res = await RequestService.CreateRequest(payload);
        hideLoading();

        if (res.data.is_success === true) {
          await showSuccess({ title: "สร้างใบคำขอสำเร็จ" });
          navigate("/user/requests", { replace: true });
        } else {
          showError();
        }
      } catch (err) {
        hideLoading();
        showError();
        console.error("CreateRequest error:", err);
      }
    },
  });

  const handleServiceTypeChange = (e) => {
    const val = e.target.value;
    validation.setFieldValue("service_type", val);

    if (!isDocumentDelivery(val)) {
      ["delivery_type", "receiver_name", "receiver_tel", "receiver_company",
        "receiver_department", "receiver_building", "receiver_floor",
        "receiver_province", "receiver_district", "receiver_map"]
        .forEach((f) => validation.setFieldValue(f, f === "delivery_type" ? null : ""));
    }

    if (String(val) === "2") {
      fetchProviders();
      validation.setFieldValue("id_sub_service_type3", "");
      validation.setFieldValue("id_provider", "");
      validation.setFieldValue("id_provider_branch", "");
      setSubServiceTypes([]);
      setTransactionProviders([]);
      setTransactionBranches([]);
    } else if (String(val) === "3") {
      validation.setFieldValue("id_provider", "");
      validation.setFieldValue("id_provider_branch", "");
      validation.setFieldValue("id_sub_service_type3", "");
      setProviders([]);
      setProviderBranches([]);
      setTransactionProviders([]);
      setTransactionBranches([]);
      fetchSubServiceTypes();
    } else {
      ["id_provider", "id_provider_branch", "id_sub_service_type3"]
        .forEach((f) => validation.setFieldValue(f, ""));
      validation.setFieldValue("receiver_name", "");
      validation.setFieldValue("receiver_tel", "");
      validation.setFieldValue("receiver_company", "");
      setProviders([]);
      setProviderBranches([]);
      setSubServiceTypes([]);
      setTransactionProviders([]);
      setTransactionBranches([]);
    }
  };

  const handleProvinceChange = (e) => {
    const val = e.target.value;
    validation.setFieldValue("receiver_province", val);
    validation.setFieldValue("receiver_district", "");
    setDistricts([]);
    if (val) fetchDistricts(val);
  };

  const handleReccurringSave = (values) => {
    const toLocalDate = (dateVal) => {
      if (!dateVal) return null;
      const d     = new Date(dateVal);
      const year  = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day   = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setRecurringConfig({
      ...values,
      days:       values.repeat_days,
      start_date: toLocalDate(values.start_date),
      end_date:   toLocalDate(values.end_date),
    });
    setShowRecurring(false);
  };

  const handleRecurringBack = () => {
      setShowRecurring(false);
  };

  const handleRecurringDelete = (clearDeliverySection = true) => {
    setShowRecurring(false);
    setRecurringConfig(null);
    if (clearDeliverySection) {
      validation.setFieldValue("delivery_section", "");
    }
};

  const fProps = (name) => ({
    id:       name,
    name,
    value:    validation.values[name],
    onChange: validation.handleChange,
    onBlur:   validation.handleBlur,
    disabled: formIsDisabled,
    className:
      "form-control" +
      (validation.touched[name] && validation.errors[name] ? " is-invalid" : ""),
  });

  const showErr = (name) =>
    validation.touched[name] && validation.errors[name]
      ? <div className="invalid-feedback">{validation.errors[name]}</div>
      : null;

  const buildMainPayload = () => {
    const values = validation.values;

    const isOtherPickup = values.pickup_location === "other";
    // ── FIX: จุดเริ่มต้น (pickup_location) เลือกมาจาก dropdown ที่สร้างจาก `buildings` (m_building) ──
    // ── เดิม lookup ใน `pickupLocations` (m_business_unit) ทำให้ได้ record ผิดตาราง (id ชนกันบังเอิญ) ──
    // ── ส่งผลให้ origin_building_name / origin_id_building ได้ค่าของ m_business_unit แทน m_building ──
    const selectedBuilding = buildings.find(
      loc => String(loc.id) === String(values.pickup_location)
    );

    const buildRequestDetail = (dest = null) => {
      const d     = dest ?? values;
      const sType = dest ? Number(dest.service_type) : Number(values.service_type);

      const destIsOtherPickup = dest ? dest.pickup_location === "other" : isOtherPickup;
      // ── FIX: destination จุดหมายอื่นๆ ก็ต้อง lookup ใน `buildings` (m_building) เช่นกัน ──
      const destBuilding = dest
        ? buildings.find(loc => String(loc.id) === String(dest.pickup_location))
        : selectedBuilding;

      const completeTimeType = Number(d.is_complete_today) === 2 ? 2 : 1;
      const completeTimeSpecific = completeTimeType === 2 && d.time_complete_today
        ? `${values.service_date}T${d.time_complete_today}:00+07:00`
        : null;
      const base = {
        id_service_type:          sType,
        id_sub_service_type:      dest ? 0 : (Number(values.delivery_type) || 0),
        complete_time_type:       completeTimeType,
        complete_time_specific:   completeTimeSpecific,
        note:                     d.note || "",
        status:                   1,
        destination_company:      "",
        destination_contact_name: "-",
        destination_department:   "",
        destination_floor:        "",
        destination_id_building:  0,
        destination_map:          "-",
        destination_name:         "",
        destination_phone_number: "",
        origin_building_name:     destIsOtherPickup ? (d.other_pickup_name || "-") : (destBuilding?.name_th || "-"),
        origin_building_map:      destIsOtherPickup ? "-" : (destBuilding?.map || "-"),
        origin_id_building:       destIsOtherPickup ? 0 : (destBuilding?.id || 0),
      };

      if (sType === 1) return {
        ...base,
        id_sub_service_type:      dest ? (Number(dest.delivery_type) || 0) : (Number(values.delivery_type) || 0),
        destination_name:         d.receiver_building === "other"
          ? (d.other_building_name || "")
          : (buildings.find(b => String(b.id) === String(d.receiver_building))?.name_th || ""),
        destination_contact_name: d.receiver_name || "-",
        destination_phone_number: d.receiver_tel,
        destination_company:      d.receiver_company,
        destination_department:   d.receiver_department,
        destination_floor:        d.receiver_floor,
        destination_map:          d.receiver_map || "",
        destination_id_province:  parseInt(d.receiver_province) || 0,
        destination_id_district:  parseInt(d.receiver_district) || 0,
        destination_id_building:  d.receiver_building === "other" ? 0 : parseInt(d.receiver_building) || 0,
      };

      if (sType === 2) {
        const isOtherBankBranch = d.id_provider_branch === "other";
        return {
          ...base,
          destination_name: isOtherBankBranch
            ? (d.other_branch_name || "")
            : "",
          destination_phone_number:       d.receiver_tel,
          destination_id_provider:        Number(d.id_provider),
          destination_id_provider_branch: isOtherBankBranch ? 0 : Number(d.id_provider_branch),
          destination_company: dest
            ? (d.provider_branch_name || "")
            : providerBranches.find(
                b => String(b.id) === String(d.id_provider_branch)
              )?.name_th || "",
          destination_map: d.receiver_map || "",
          destination_contact_name: "-",
        };
      }

      if (sType === 3) {
        const isOtherTxBranch = d.id_provider_branch === "other";
        return {
          ...base,
          id_sub_service_type:            Number(d.id_sub_service_type3) || 0,
          destination_id_provider:        Number(d.id_provider) || 0,
          destination_id_provider_branch: isOtherTxBranch ? 0 : (Number(d.id_provider_branch) || 0),
          destination_company:      transactionProviders.find(
            p => String(p.id) === String(d.id_provider)
          )?.name_th || "",
          destination_name: isOtherTxBranch
            ? (d.other_branch_name || "")
            : (transactionBranches.find(
                b => String(b.id) === String(d.id_provider_branch)
              )?.name_th || ""),
          destination_map:          d.receiver_map || "",
          destination_contact_name: "-",
          destination_phone_number: "",
        };
      }

      return base;
    };

    return {
      sender_id_user:         dataUser?.id,
      // sender_id_bu:           isOtherPickup ? 0 : (Number(values.pickup_location) || 0),
      sender_id_bu:           dataUser?.id_business_unit,
      sender_name:            `${dataUser?.name} ${dataUser?.surname}`,
      sender_tel:             dataUser?.tel,
      sender_department:      dataUser?.department,
      service_date:           values.service_date,
      document_type:          Number(values.document_secret),
      delivery_section:       Number(values.delivery_section),
      id_workflow_status:     1,
      id_messenger:           0,
      status:                 1,
      create_by:              getUserEmail(),
      request_details: [
        buildRequestDetail(),
        ...destinations.map((dest) => buildRequestDetail(dest)),
      ],
      request_recurring: recurringConfig
        ? {
            ...buildRecurringPayload(recurringConfig),
            id:         0,
            id_request: 0,
          }
        : null,
    };
  };

  return {
    dataUser, formIsDisabled,
    pickupLocations, loadingLocations,
    serviceTypes,
    // ── main form banking ──
    providers, providerBranches, loadingProviders, loadingBranches,
    fetchProviders, fetchProviderBranches,
    setProviders, setProviderBranches,
    // ── extra destinations banking ──
    destProviders, destProviderBranches, loadingDestProviders, loadingDestBranches,
    fetchDestProviders, fetchDestProviderBranches,
    setDestProviders, setDestProviderBranches,
    subServiceTypes, loadingSubServiceTypes,
    fetchSubServiceTypes,
    buildings, provinces, districts, loadingDistricts,
    fetchDistricts,
    destDistricts, loadingDestDistricts, fetchDestDistricts,
    isCloneData, setIsCloneData, cloneRefNo, setCloneRefNo,
    setDataDetail,
    showRecurring, setShowRecurring, recurringConfig,
    showRecurringPreview, setShowRecurringPreview,
    recurringPreviewDates,
    createdRefNo,
    refNo,
    isViewMode,
    handleReccurringSave, handleRecurringBack, handleRecurringDelete,
    validation, fProps, showErr,
    handleServiceTypeChange,
    handleProvinceChange,
    destinations, addDestination, removeDestination, updateDestination,
    applyCloneDestinations,
    MAX_EXTRA_DESTINATIONS,
    buildMainPayload,
    // ── Transaction ──
    transactionProviders, loadingTransactionProviders,
    transactionBranches,  loadingTransactionBranches,
    fetchTransactionProviders,
    fetchTransactionBranches,
    requestDetails,
    // ── Destination validation ──
    validateDestinations, destinationErrors, showDestErr, setDestinationErrorsField,
  };
};